import { forwardRef, useMemo } from 'react';
import { PiStackDuotone } from 'react-icons/pi';

import { useSortable } from '@dnd-kit/sortable';
import { useForkRef } from '@spazious/react-hooks';

import { getColumnId } from './utils/index.js';
import { DataTableColumn, DataTableColumnProps } from './DataTableColumn.js';
import { DataTableRow } from './DataTableTypes.js';

export type DataTableColumnDraggableElement = HTMLDivElement;

export type DataTableColumnDraggableDragHandler = React.ReactElement;

export type DataTableColumnDraggableProps<TRow extends DataTableRow, TContext = unknown> = Omit<
  DataTableColumnProps<TRow, TContext>,
  'children'
> & {
  children?: React.ReactNode | React.FC<{ dragHandler?: DataTableColumnDraggableDragHandler }>;
};

export const DataTableColumnDraggable = forwardRef(
  <TRow extends DataTableRow, TContext = unknown>(
    {
      draggable,
      children,
      column,
      style: styleProp,
      ...restProps
    }: DataTableColumnDraggableProps<TRow, TContext>,
    ref: React.ForwardedRef<DataTableColumnDraggableElement>
  ) => {
    const id = getColumnId(column);
    const isDraggable = draggable || column.draggable !== false;

    const { listeners, attributes, setNodeRef, transform, transition, isDragging } = useSortable({
      id,
      disabled: !isDraggable,
    });

    const columnRef = useForkRef<DataTableColumnDraggableElement>(ref, setNodeRef);

    const style = {
      zIndex: isDragging ? 1 : 0,
      opacity: isDragging ? 0.5 : 1,
      ...styleProp,
      transition,
      transform: `translate(${transform?.x ?? 0}px, 0px)`,
    };

    const childrenToRender = useMemo(
      () =>
        typeof children === 'function'
          ? children({
              dragHandler: isDraggable ? (
                <PiStackDuotone fontSize="small" {...listeners} {...attributes} />
              ) : undefined,
            })
          : children,
      [children, isDraggable, listeners, attributes]
    );

    return (
      <DataTableColumn ref={columnRef} column={column as any} style={style} {...restProps}>
        {childrenToRender}
      </DataTableColumn>
    );
  }
);
